import { useNavigate } from 'react-router-dom';
import {
  HandHeart, ShieldCheck, Sparkles, Compass, HeartHandshake,
  ArrowRight, Quote, CheckCircle2, Users, Target, Telescope,
} from 'lucide-react';

const IMG_TEAM = 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxlZHVjYXRpb24lMjBhZHZpc29yJTIwbWVldGluZ3xlbnwwfHx8fDE3ODQyMzgzMDh8MA&ixlib=rb-4.1.0&q=85&auto=format';

const VALUES = [
  {
    icon: HandHeart,
    title: 'Student-First Approach',
    body: "Every recommendation we make is based on what's best for the student, not what's easiest or most convenient.",
  },
  {
    icon: ShieldCheck,
    title: 'Integrity & Transparency',
    body: 'Honest guidance, clear communication, and ethical practices throughout the admissions process.',
  },
  {
    icon: Sparkles,
    title: 'Excellence',
    body: 'High-quality support, delivered with the professionalism and personalized attention every student deserves.',
  },
  {
    icon: Compass,
    title: 'Accessibility',
    body: 'Quality education should be within reach for everyone. We help students explore options that align with their goals and circumstances.',
  },
  {
    icon: HeartHandshake,
    title: 'Commitment',
    body: "From the first consultation to enrolment and beyond, we're dedicated to supporting students at every step.",
  },
];

const DIFFERENTIATORS = [
  {
    title: 'Personalized, not templated.',
    body: 'No two students are the same. We tailor every plan to your goals, interests, and circumstances — never one-size-fits-all.',
  },
  {
    title: 'End-to-end guidance.',
    body: 'Career counselling, university selection, applications, scholarships, visa support, and pre-departure — all under one roof.',
  },
  {
    title: 'Long-term relationships.',
    body: 'Our support doesn\u2019t stop at enrolment. We stay with our students long after they land in class.',
  },
];

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="bg-brand-alabaster font-sans-clean text-brand-obsidian" data-testid="about-page">
      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-4">
              About True Global Education
            </div>
            <h1 className="font-serif-editorial text-5xl md:text-6xl leading-[1.05] tracking-tight">
              Making quality education <span className="text-brand-pine italic">accessible, transparent, and achievable</span> — for every student.
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-lg text-brand-slate leading-relaxed">
              We help Canadian and international students make confident academic decisions that lead to
              meaningful careers — with honest advice, personalised support, and no pressure at any step.
            </p>
          </div>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20 border-t border-brand-border/50">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-3">
              Our Story
            </div>
            <h2 className="font-serif-editorial text-3xl md:text-4xl leading-tight">
              Founded to bridge <span className="italic text-brand-pine">the guidance gap.</span>
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-5 text-brand-slate leading-relaxed">
            <p>
              True Global Education was founded with a simple yet powerful mission: to make quality
              international education accessible, transparent, and achievable for every student. We
              recognised that many students with the potential to study abroad often struggle with
              confusing admission processes, limited guidance, and a lack of reliable information.
            </p>
            <p>
              Our goal was to bridge that gap by providing honest advice and personalised support
              throughout every stage of the journey.
            </p>
            <p className="text-brand-obsidian font-serif-editorial text-xl leading-snug pt-2">
              We believe that studying abroad is more than just earning a degree — it&rsquo;s about
              creating opportunities, building confidence, and shaping successful futures.
            </p>
          </div>
        </div>
      </section>

      {/* WHO WE ARE — with image */}
      <section className="bg-white border-y border-brand-border/50">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-3">
                Who We Are
              </div>
              <h2 className="font-serif-editorial text-3xl md:text-4xl leading-tight">
                A team that treats every student like their <span className="italic text-brand-pine">only student.</span>
              </h2>
              <div className="mt-8 space-y-5 text-brand-slate leading-relaxed">
                <p>
                  True Global Education is led by a team of passionate education professionals dedicated
                  to helping students achieve their academic and career goals. With extensive knowledge
                  of international education systems, admissions, and student support services, we work
                  closely with each student to identify the best educational pathway based on their
                  aspirations, academic background, and budget.
                </p>
                <p>
                  Our commitment is to simplify what can often be a complex process, allowing students
                  and their families to make informed decisions with confidence.
                </p>
              </div>
            </div>
            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
                <img
                  src={IMG_TEAM}
                  alt="True Global Education advisor session"
                  className="w-full h-full object-cover"
                />
                <div className="absolute -bottom-2 -left-2 bg-white rounded-2xl px-5 py-4 shadow-lg max-w-xs">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-brand-pine font-bold">Advisor Session</div>
                  <div className="font-serif-editorial text-base leading-snug mt-1">One conversation. One personalised plan.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT INSPIRES US — quote panel */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center">
          <Quote size={36} className="text-brand-coral mx-auto opacity-80" />
          <blockquote className="font-serif-editorial text-3xl md:text-4xl leading-[1.2] tracking-tight mt-8 max-w-3xl mx-auto">
            Every student&rsquo;s journey is unique &mdash; and that&rsquo;s what inspires us every day.
            Seeing students receive admission offers, scholarships, and visas, and ultimately succeed in
            their careers, is the driving force behind everything we do.
          </blockquote>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mt-10">
            What Inspires Us
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-brand-mist/60 border-y border-brand-border/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="max-w-2xl mb-16">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-3">Our Values</div>
            <h2 className="font-serif-editorial text-3xl md:text-5xl tracking-tight leading-tight">
              What we stand for &mdash; every single day.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {VALUES.map((v) => (
              <div
                key={v.title}
                className="bg-white rounded-3xl p-8 border border-brand-border/50 hover:-translate-y-1 hover:shadow-lg hover:border-brand-pine/40 transition-[transform,box-shadow,border-color]"
                data-testid={`value-${v.title.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <div className="w-11 h-11 rounded-xl bg-brand-pine/10 flex items-center justify-center">
                  <v.icon size={19} className="text-brand-pine" />
                </div>
                <h3 className="font-serif-editorial text-xl mt-6 leading-snug">{v.title}</h3>
                <p className="text-sm text-brand-slate mt-3 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT MAKES US DIFFERENT */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-3">
              What Makes Us Different
            </div>
            <h2 className="font-serif-editorial text-3xl md:text-4xl leading-tight">
              We&rsquo;re not a factory. We&rsquo;re your <span className="italic text-brand-pine">advisor.</span>
            </h2>
            <p className="text-brand-slate mt-8 leading-relaxed">
              At True Global Education, we understand that no two students are the same. Our focus on
              transparency, personalised service, and genuine student success sets us apart from
              traditional education consultancies.
            </p>
          </div>
          <div className="lg:col-span-7 space-y-5">
            {DIFFERENTIATORS.map((d) => (
              <div
                key={d.title}
                className="bg-white border border-brand-border/50 rounded-2xl p-7 flex gap-5"
                data-testid={`differentiator-${d.title.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <div className="w-9 h-9 rounded-full bg-brand-coral/10 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={17} className="text-brand-coral-deep" />
                </div>
                <div>
                  <h3 className="font-serif-editorial text-lg leading-snug">{d.title}</h3>
                  <p className="text-sm text-brand-slate mt-2 leading-relaxed">{d.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="bg-brand-obsidian text-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="border border-white/10 rounded-3xl p-9 bg-white/[0.02]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-coral/15 flex items-center justify-center">
                  <Target size={17} className="text-brand-coral" />
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-coral font-bold">Our Mission</div>
              </div>
              <p className="mt-6 font-serif-editorial text-xl md:text-2xl leading-snug tracking-tight">
                To empower students with trusted guidance, personalised support, and access to world-class
                educational opportunities &mdash; helping them achieve their academic ambitions and build
                successful global careers.
              </p>
            </div>
            <div className="border border-white/10 rounded-3xl p-9 bg-white/[0.02]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-coral/15 flex items-center justify-center">
                  <Telescope size={17} className="text-brand-coral" />
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-coral font-bold">Our Vision</div>
              </div>
              <p className="mt-6 font-serif-editorial text-xl md:text-2xl leading-snug tracking-tight">
                To become one of Canada&rsquo;s most trusted international education consultancies &mdash;
                transforming lives through education, fostering lifelong success, and making global
                learning opportunities accessible to students everywhere.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand-pine text-white">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <Users size={30} className="text-brand-coral mx-auto opacity-90" />
          <h2 className="font-serif-editorial text-3xl md:text-5xl leading-tight mt-6">
            Curious what a session with us looks like?
          </h2>
          <p className="text-white/70 mt-5 max-w-xl mx-auto leading-relaxed">
            30 minutes. No pressure. Just an honest conversation about where you are and where you want to go.
          </p>
          <button
            onClick={() => navigate('/contact')}
            data-testid="about-cta"
            className="mt-10 inline-flex items-center gap-2 bg-brand-coral hover:bg-brand-coral-deep text-white text-sm md:text-base font-semibold px-8 py-4 rounded-full transition-colors"
          >
            Book a Free Consultation <ArrowRight size={16} />
          </button>
        </div>
      </section>
    </div>
  );
}
